import React from "react";
import qs from "query-string";
import { withI18n } from "react-i18next";
import styled from "styled-components";
import { withRouter } from "react-router-dom";
import { iRootState } from "../../store";
import { connect } from "react-redux";
import { cleanCache, getCountryCode } from "../../helpers/index";
import TalentCredentialStep from "./TalentCredentialStep";
import TalentSellingFeatures from "./TalentSellingFeatures";
import Modal from "../Common/Modal";
import ProgressiveImage from "react-progressive-image";

import poster1 from "../../images/talent/poster1.jpg";
import poster2 from "../../images/talent/poster2.jpg";
import poster3 from "../../images/talent/poster3.jpg";
import poster4 from "../../images/talent/poster4.jpg";
import poster5 from "../../images/talent/poster5.jpg";
import poster6 from "../../images/talent/poster6.jpg";

import poster1Thumb from "../../images/talent/thumbnails/poster1_thumb.jpg";
import poster2Thumb from "../../images/talent/thumbnails/poster2_thumb.jpg";
import poster3Thumb from "../../images/talent/thumbnails/poster3_thumb.jpg";
import poster4Thumb from "../../images/talent/thumbnails/poster4_thumb.jpg";
import poster5Thumb from "../../images/talent/thumbnails/poster5_thumb.jpg";
import poster6Thumb from "../../images/talent/thumbnails/poster6_thumb.jpg";

const posters = [
  { full: poster1, thumb: poster1Thumb },
  { full: poster2, thumb: poster2Thumb },
  { full: poster3, thumb: poster3Thumb },
  { full: poster4, thumb: poster4Thumb },
  { full: poster5, thumb: poster5Thumb },
  { full: poster6, thumb: poster6Thumb },
];

interface IProps extends StateProps, DispatchProps {
  t?: any;
  history: any;
  location: any;
  match: any;
}

class TalentSignup extends React.Component<IProps> {
  async componentWillMount() {
    cleanCache();

    const qsArgs = this.props.location.search
      ? { ...qs.parse(this.props.location.search) } as any
      : {};
    if (qsArgs.dev && qsArgs.ctry) {
      await this.props.setForceRequesterCountryCode(qsArgs.ctry.toString());
    } else {
      await this.props.setRequesterCountryCode();
    }
  }

  render() {
    const { t, countryCode } = this.props;
    const userCountryCode = countryCode || getCountryCode();

    return (
      <PageWrapper>
        {/* Left Poster Column - extends beyond left edge */}
        <PosterColumnLeft>
          <PosterColumnInnerLeft>
            {/* Column 1 - offset up */}
            <PosterCol style={{ marginTop: "-60px" }}>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <ProgressiveImage key={`left1-${i}`} src={posters[i].full} placeholder={posters[i].thumb}>
                  {(src: string, loading: boolean) => (
                    <Poster src={src} alt="" loading={loading} />
                  )}
                </ProgressiveImage>
              ))}
            </PosterCol>
            {/* Column 2 - normal position */}
            <PosterCol>
              {[3, 4, 5, 0, 1, 2].map((i, idx) => (
                <ProgressiveImage key={`left2-${idx}`} src={posters[i].full} placeholder={posters[i].thumb}>
                  {(src: string, loading: boolean) => (
                    <Poster src={src} alt="" loading={loading} />
                  )}
                </ProgressiveImage>
              ))}
            </PosterCol>
          </PosterColumnInnerLeft>
          <PosterOverlayLeft />
        </PosterColumnLeft>

        {/* Center Content */}
        <ContentWrapper>
          <HeroSection>
            <HeroTitle>
              {t("START YOUR")} <HighlightText>{t("FREE TRIAL")}</HighlightText> {t("TO ACCESS THE LATEST VERTICAL MICRODRAMA CASTING OPPORTUNITIES")}
            </HeroTitle>
            <HeroSubtitle>
              {t("Big Stories, Small Screen, Start your free trial today")}
            </HeroSubtitle>
          </HeroSection>

          <FormCard>
            {/* <Divider /> */}
            <FormTitle>{t("Let's create your Free Trial")}</FormTitle>
            <TalentCredentialStep
              {...this.props}
              countryCode={userCountryCode}
            />
          </FormCard>

          <TalentSellingFeatures />
        </ContentWrapper>

        {/* Right Poster Column - extends beyond right edge (large screens) */}
        <PosterColumnRight>
          <PosterColumnInnerRight>
            {/* Column 1 - normal position */}
            <PosterCol>
              {[3, 4, 5, 0, 1, 2].map((i, idx) => (
                <ProgressiveImage key={`right1-${idx}`} src={posters[i].full} placeholder={posters[i].thumb}>
                  {(src: string, loading: boolean) => (
                    <Poster src={src} alt="" loading={loading} />
                  )}
                </ProgressiveImage>
              ))}
            </PosterCol>
            {/* Column 2 - offset down */}
            <PosterCol style={{ marginTop: "60px" }}>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <ProgressiveImage key={`right2-${i}`} src={posters[i].full} placeholder={posters[i].thumb}>
                  {(src: string, loading: boolean) => (
                    <Poster src={src} alt="" loading={loading} />
                  )}
                </ProgressiveImage>
              ))}
            </PosterCol>
          </PosterColumnInnerRight>
          <PosterOverlayRight />
        </PosterColumnRight>

        {/* Right Poster Column - single column for medium screens (768px-920px) */}
        <PosterColumnRightMedium>
          <PosterColumnInnerRight>
            <PosterCol>
              {[0, 1, 2].map((i) => (
                <ProgressiveImage key={`right-med-${i}`} src={posters[i].full} placeholder={posters[i].thumb}>
                  {(src: string, loading: boolean) => (
                    <Poster src={src} alt="" loading={loading} />
                  )}
                </ProgressiveImage>
              ))}
            </PosterCol>
          </PosterColumnInnerRight>
          <PosterOverlayRight />
        </PosterColumnRightMedium>

        {/* Mobile Background */}
        <MobileBackground />
      </PageWrapper>
    );
  }
}

interface StateProps {
  countryCode: string;
}

function mapStateToProps(state: iRootState): StateProps {
  return {
    countryCode: state.referencesModel.countryCode,
  };
}

interface DispatchProps {
  setRequesterCountryCode: (fallback?: string | null) => void;
  setForceRequesterCountryCode: (countryCode: string) => void;
}

function mapDispatchToProps(dispatch: any): DispatchProps {
  return {
    setRequesterCountryCode: dispatch.referencesModel.setRequesterCountryCode,
    setForceRequesterCountryCode: dispatch.referencesModel.setForceRequesterCountryCode,
  };
}

export default withI18n()(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(TalentSignup))
);

// Navy blue background
const PageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  background-color: #0a1f44;
  position: relative;
  overflow: hidden;

  @media all and (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const MobileBackground = styled.div`
  display: none;

  @media all and (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url(${poster4});
    background-size: cover;
    background-position: center;
    z-index: 0;

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(10, 31, 68, 0.75);
    }
  }
`;

// Left poster column - positioned to extend beyond left edge
const PosterColumnLeft = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 380px;
  min-height: 100%;
  overflow: hidden;

  @media all and (max-width: 1378px) and (min-width: 1201px) {
    left: -100px;
  }

  @media all and (max-width: 1200px) {
    display: none;
  }
`;

const PosterColumnInnerLeft = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px;
`;

// Right poster column - positioned to extend beyond right edge (large screens)
const PosterColumnRight = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 380px;
  min-height: 100%;
  overflow: hidden;

  @media all and (max-width: 1378px) and (min-width: 1201px) {
    right: -100px;
  }

  @media all and (max-width: 920px) {
    display: none;
  }
`;

// Right poster column - single column for medium screens (768px-920px)
const PosterColumnRightMedium = styled.div`
  display: none;
  position: absolute;
  top: 0;
  right: 0;
  width: 200px;
  min-height: 100%;
  overflow: hidden;

  @media all and (max-width: 920px) and (min-width: 769px) {
    display: block;
  }
`;

const PosterColumnInnerRight = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px;
  justify-content: flex-end;
`;

// Single column of posters
const PosterCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 180px;
  flex-shrink: 0;
`;

// Individual poster
const Poster = styled.img<{ mirrored?: boolean; loading?: boolean }>`
  width: 100%;
  height: auto;
  border-radius: 8px;
  object-fit: cover;
  filter: ${props => props.loading ? 'blur(10px)' : 'blur(0)'};
  transition: filter 0.3s ease-out;
  ${props => props.mirrored && `transform: scaleX(-1);`}
`;

// Transparency overlay - fade from left edge toward center
const PosterOverlayLeft = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  min-height: 100%;
  background: linear-gradient(
    to right,
    rgba(10, 31, 68, 0.85) 0%,
    rgba(10, 31, 68, 0.5) 50%,
    rgba(10, 31, 68, 0.2) 100%
  );
  pointer-events: none;
`;

// Transparency overlay - fade from right edge toward center
const PosterOverlayRight = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  min-height: 100%;
  background: linear-gradient(
    to left,
    rgba(10, 31, 68, 0.85) 0%,
    rgba(10, 31, 68, 0.5) 50%,
    rgba(10, 31, 68, 0.2) 100%
  );
  pointer-events: none;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  max-width: 650px;
  width: 100%;
  z-index: 1;

  @media all and (max-width: 1200px) and (min-width: 769px) {
    align-items: flex-start;
    margin-left: 40px;
    margin-right: auto;
  }

  @media all and (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const HeroSection = styled.div`
  text-align: left;
  margin-bottom: 24px;
  width: 100%;
  max-width: 550px;

  @media all and (max-width: 1200px) and (min-width: 769px) {
    text-align: left;
  }

  @media all and (max-width: 768px) {
    margin-bottom: 20px;
    text-align: center;
  }
`;

const HeroTitle = styled.h1`
  font-size: 30px;
  font-weight: 800;
  color: #ffffff;
  line-height: 1.15;
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 2px;

  @media all and (max-width: 768px) {
    font-size: 26px;
  }

  @media all and (max-width: 520px) {
    font-size: 22px;
  }
`;

const HighlightText = styled.span`
  color: #2ed2ff;
`;

const HeroSubtitle = styled.p`
  font-size: 16px;
  color: #ffffff;
  margin: 0;

  @media all and (max-width: 520px) {
    font-size: 14px;
  }
`;

const FormCard = styled(Modal)`
  width: 100%;
  max-width: 550px;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 32px 48px 40px;
  margin-bottom: 32px;
  border-top: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  @media all and (max-width: 520px) {
    padding: 24px 20px 32px;
    margin-bottom: 24px;
  }
`;

const Divider = styled.div`
  width: 80px;
  height: 4px;
  background-color: #0a1f44;
  margin: 0 auto 24px;
`;

const FormTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #0a1f44;
  text-align: center;
  margin: 0 0 16px 0;

  @media all and (max-width: 520px) {
    font-size: 18px;
  }
`;
